<template>
  <main class="grow tools-hub">
    <section class="tools-hero">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="tools-hero-inner">
          <h1>Free tools for client work, billing and planning.</h1>
          <p class="tools-lede">
            Small, fast utilities that work right on the page — no signup, no
            watermark, no account required. Built by the team behind Workspace369.
          </p>
        </div>
      </div>
    </section>

    <section class="tools-grid-section">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-16">
        <div class="tools-grid">
          <NuxtLink
            v-for="tool in tools"
            :key="tool.path"
            :to="localePath(tool.path)"
            class="tools-card"
          >
            <span>{{ tool.badge }}</span>
            <h3>{{ tool.title }}</h3>
            <p>{{ tool.copy }}</p>
            <em>Open the tool <i aria-hidden="true">-&gt;</i></em>
          </NuxtLink>
        </div>
      </div>
    </section>

    <Cta03 />
  </main>
</template>

<script setup>
import { localizedUrl } from '~/data/locales';

const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = 'https://workspace369.com';
const canonicalUrl = computed(() => localizedUrl(siteUrl, '/tools', canonicalLocaleCode.value));

const tools = [
  { badge: 'Client delivery', title: 'SEO deliverables tracker', copy: 'Plan monthly work, owners, decisions and completion evidence. Export a CSV or print your plan.', path: '/tools/seo-deliverables-tracker' },
  { badge: 'Scope changes', title: 'Scope-change request builder', copy: 'Compare the original scope with requested work, proposed fees and delivery dates before seeking a decision.', path: '/tools/scope-change-request-generator' },
  { badge: 'Agency planning', title: 'Billable utilization calculator', copy: 'Calculate utilization after leave, inspect your target gap and download the scenario.', path: '/tools/billable-utilization-calculator' },
  { badge: 'Agency planning', title: 'Agency capacity planner', copy: 'Check proposed client work against availability by role, including hidden skill bottlenecks.', path: '/tools/agency-capacity-planner' },
  {
    badge: 'Scheduling',
    title: 'Free booking page generator',
    copy: 'Add your services, lengths, prices and days, and preview the real booking page clients will use. Make it live with a trial.',
    path: '/tools/booking-page-generator',
  },
  {
    badge: 'Invoicing',
    title: 'Free invoice generator',
    copy: 'Create a clean, printable invoice with items, tax, and totals — download as PDF, no signup.',
    path: '/tools/invoice-generator',
  },
  {
    badge: 'Invoicing',
    title: 'Free invoice template',
    copy: 'CSV invoice templates for Excel and Google Sheets — hourly, fixed-fee, and retainer formats.',
    path: '/tools/invoice-template',
  },
  {
    badge: 'Invoicing',
    title: 'Blank invoice template',
    copy: 'A printable blank invoice in US Letter or A4. Print it or save it as a PDF to fill in by hand or on screen.',
    path: '/tools/blank-invoice-template',
  },
  {
    badge: 'Quoting',
    title: 'Free estimate template',
    copy: 'A clean estimate template with valid-until date, downloadable as a CSV for Excel or Sheets.',
    path: '/tools/estimate-template',
  },
  {
    badge: 'Getting paid',
    title: 'Payment reminder generator',
    copy: 'Write polite, firm, or final payment reminders for overdue invoices in one click.',
    path: '/tools/payment-reminder-generator',
  },
  {
    badge: 'Pricing',
    title: 'Hourly rate calculator',
    copy: 'Work out the hourly and day rate you need from income goals, hours, expenses, and margin.',
    path: '/tools/hourly-rate-calculator',
  },
  {
    badge: 'Pricing',
    title: 'Consulting rate calculator',
    copy: 'Turn an annual income goal into the hourly, day, and project rate you need to charge.',
    path: '/tools/consulting-rate-calculator',
  },
  {
    badge: 'Pricing',
    title: 'Profit margin calculator',
    copy: 'Enter the job price, direct costs, and allocated overhead to estimate job profit or loss, margin, and markup. Free, instant, no signup.',
    path: '/tools/profit-margin-calculator',
  },
  {
    badge: 'Getting paid',
    title: 'Deposit calculator',
    copy: 'Work out the up-front deposit, remaining balance, and a simple billing schedule.',
    path: '/tools/deposit-calculator',
  },
  {
    badge: 'Getting paid',
    title: 'Payment processing fee calculator',
    copy: 'See what card and ACH fees really cost per invoice across Stripe, HoneyBook, FreshBooks, Jobber, and QuickBooks.',
    path: '/tools/payment-processing-fee-calculator',
  },
  {
    badge: 'Getting paid',
    title: 'Upwork vs Fiverr fee calculator',
    copy: 'What each platform leaves you on a job, what the client pays, what bidding costs and when the money lands.',
    path: '/tools/upwork-vs-fiverr-fee-calculator',
  },
  {
    badge: 'Getting paid',
    title: 'Invoice due date calculator',
    copy: 'Pick the terms, get the due date, the reminder dates, and the first late day.',
    path: '/tools/invoice-due-date-calculator',
  },
  {
    badge: 'Client communication',
    title: 'Email signature generator',
    copy: 'Build a clean email signature with your photo, booking link and brand color for Gmail, Outlook or Apple Mail.',
    path: '/tools/email-signature-generator',
  },
  {
    badge: 'Getting started',
    title: 'Business name generator',
    copy: 'Name ideas for a freelance business, studio or consultancy, with .com checks and trademark tips.',
    path: '/tools/business-name-generator',
  },
  {
    badge: 'Getting started',
    title: 'Freelancer bio generator',
    copy: 'A short bio, website about section, LinkedIn headlines and a mission statement from five quick answers.',
    path: '/tools/freelancer-bio-generator',
  },
  {
    badge: 'Proposals',
    title: 'Freelance contract template',
    copy: 'Fill in scope, fee, deposit and terms, then download a plain-language agreement for Word or PDF. Not legal advice.',
    path: '/tools/freelance-contract-template',
  },
  {
    badge: 'Proposals',
    title: 'Proposal follow-up generator',
    copy: 'Ready-to-send follow-up emails and texts for proposals sitting unanswered.',
    path: '/tools/proposal-follow-up-generator',
  },
  {
    badge: 'Getting paid',
    title: 'Late fee calculator',
    copy: 'Calculate the late fee on an overdue invoice from the fee rate and days late.',
    path: '/tools/late-fee-calculator',
  },
  {
    badge: 'Pricing',
    title: 'Retainer pricing calculator',
    copy: 'Price a monthly retainer from hours, rate, overhead, and margin — with the annual value.',
    path: '/tools/retainer-calculator',
  },
];

const metaTitle = 'Free Online Tools for Invoicing & Billing | Workspace369';
const metaDescription =
  'Free online tools from Workspace369: invoice generator, payment reminder generator, hourly rate calculator and late fee calculator. No signup needed.';

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: 'description', content: metaDescription },
    { name: 'twitter:title', content: metaTitle },
    { name: 'twitter:description', content: metaDescription },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'og:title', content: metaTitle },
    { property: 'og:description', content: metaDescription },
  ],
  link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl.value }],
  script: [
    {
      key: 'workspace369-tools-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        '@id': `${canonicalUrl.value}#tool-list`,
        name: 'Free tools for client work, billing and planning.',
        url: canonicalUrl.value,
        numberOfItems: tools.length,
        itemListElement: tools.map((tool, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: tool.title,
          url: localizedUrl(siteUrl, tool.path, canonicalLocaleCode.value),
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.tools-hero {
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 0%, rgb(27 167 206 / 0.13), transparent 30rem),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  padding: 8.5rem 0 4rem;
}

.dark .tools-hero {
  background:
    radial-gradient(circle at 12% 12%, rgb(27 167 206 / 0.18), transparent 28rem),
    linear-gradient(180deg, #020617 0%, #0f172a 100%);
}

.tools-hero-inner {
  max-width: 44rem;
  margin: 0 auto;
  text-align: center;
}

.tools-hero h1 {
  margin-top: 0.9rem;
  color: #020617;
  font-size: clamp(2.4rem, 6vw, 3.9rem);
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: -0.01em;
  text-wrap: balance;
}

.dark .tools-hero h1 {
  color: #ffffff;
}

.tools-lede {
  margin-top: 1.15rem;
  color: #475569;
  font-size: 1rem;
  line-height: 1.75;
}

.dark .tools-lede {
  color: #cbd5e1;
}

.tools-grid-section {
  background: #ffffff;
}

.dark .tools-grid-section {
  background: #020617;
}

.tools-grid {
  display: grid;
  gap: 1rem;
}

.tools-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 1.35rem 1.3rem 1.2rem;
  text-decoration: none;
  transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}

.dark .tools-card {
  border-color: #243247;
  background: #07101f;
}

.tools-card:hover {
  border-color: rgb(27 167 206 / 0.45);
  box-shadow: 0 16px 40px rgb(15 23 42 / 0.08);
  transform: translateY(-2px);
}

.tools-card > span {
  color: #0e7490;
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
}

.dark .tools-card > span {
  color: #67e8f9;
}

.tools-card h3 {
  margin-top: 0.65rem;
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 850;
}

.dark .tools-card h3 {
  color: #f8fafc;
}

.tools-card p {
  margin-top: 0.55rem;
  color: #64748b;
  font-size: 0.86rem;
  line-height: 1.6;
}

.dark .tools-card p {
  color: #94a3b8;
}

.tools-card em {
  margin-top: auto;
  padding-top: 1rem;
  color: #0e7490;
  font-size: 0.8rem;
  font-style: normal;
  font-weight: 800;
}

.dark .tools-card em {
  color: #67e8f9;
}

@media (min-width: 768px) {
  .tools-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .tools-hero {
    padding: 7rem 0 3rem;
  }
}
</style>
